import { useFetch } from "../../hooks/useFetch";
import { useParams, useNavigate } from "react-router-dom";
import { calculateTax, calculateTotal } from "../../utils/taxCalculations";
import Loading from "../Loading";
import noImage from "../images/no-image.png";

import "./OrderDetails.css";

const OrderDetails = () => {
  let { id } = useParams();
  const navigate = useNavigate();
  const { data: order, isPending, error } = useFetch(`/api/orders/${id}`);

  const calculateCartTotal = () => {
    // Calculate subtotal from products to ensure accuracy
    if (!order || !order.products) return 0;
    return order.products.reduce((total, product) => {
      return total + product.casePrice * product.quantity;
    }, 0);
  };

  const getStatusBadgeClass = (orderState) => {
    const stateMap = {
      placed: "status-placed",
      submitted: "status-submitted",
      fulfilled: "status-fulfilled",
      cancelled: "status-cancelled",
    };
    return stateMap[orderState] || "status-default";
  };

  const getStatusDisplayText = (orderState) => {
    const displayMap = {
      placed: "PLACED",
      pending: "PLACED",
      submitted: "PLACED",
      fulfilled: "PICKED UP",
      cancelled: "CANCELLED",
    };
    return displayMap[orderState?.toLowerCase()] || "PENDING";
  };

  const getTemperatureDisplayText = (tempCode) => {
    const tempMap = {
      f: "Frozen",
      d: "Dry",
      r: "Refrigerated",
    };
    return tempMap[tempCode?.toLowerCase()] || tempCode;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatShortDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleRow = (product) => {
    let {
      productId,
      description,
      quantity,
      casePrice,
      unitSize,
      temperature,
      thumbnailUrl,
      thumbnail,
    } = product;
    let rowTotal = casePrice * quantity;
    let amount = rowTotal / 100;

    const displayQuantity = quantity > 0 ? quantity : "Out of Stock";
    const formattedAmount = amount.toFixed(2);

    const imageUrl =
      (thumbnailUrl || thumbnail) &&
      (thumbnailUrl || thumbnail).startsWith("https://storage.googleapis.com/")
        ? thumbnailUrl || thumbnail
        : noImage;

    return (
      <div key={productId} className="cart-item">
        <div className="cart-item-image">
          <img src={imageUrl} alt={description} />
        </div>
        <div className="cart-item-details">
          <p className="cart-item-description">{description}</p>
          {unitSize && <p className="cart-item-size">{unitSize}</p>}
          {temperature && (
            <span className={`temp-badge temp-${temperature.toLowerCase()}`}>
              {getTemperatureDisplayText(temperature)}
            </span>
          )}
          <div className="cart-item-quantity-price">
            <span>Qty: {displayQuantity}</span>
            <span className="cart-item-price">${formattedAmount}</span>
          </div>
          <p className="cart-item-id">Product #: {productId}</p>
        </div>
      </div>
    );
  };

  const handleCartTotal = () => {
    return calculateTotal(calculateCartTotal(), order.taxRate);
  };

  const handleTaxTotal = (subtotal) => {
    if (
      order?.stripeAmounts?.tax !== undefined &&
      order?.stripeAmounts?.tax !== null
    ) {
      return Number(order.stripeAmounts.tax || 0);
    }

    // Accept optional subtotal parameter, otherwise calculate from cart
    const sub = subtotal !== undefined ? subtotal : calculateCartTotal();
    return calculateTax(sub, order.taxRate);
  };

  const getDeliveryFee = () => {
    if (
      order.fulfillment?.type === "Delivery" &&
      order.fulfillment?.deliveryFee
    ) {
      return Number(order.fulfillment.deliveryFee);
    }
    return 0;
  };

  const getDiscountTotal = () => {
    if (
      order?.stripeAmounts?.discount !== undefined &&
      order?.stripeAmounts?.discount !== null
    ) {
      return Number(order.stripeAmounts.discount || 0);
    }

    const subtotal = calculateCartTotal(); // Use calculated total instead of accumulated
    const tax = handleTaxTotal(subtotal); // Pass subtotal as parameter
    const deliveryFee = getDeliveryFee();
    const preDiscountTotal = subtotal + tax + deliveryFee;
    const discount = Math.max(
      0,
      preDiscountTotal - Number(order.orderTotal || 0)
    );

    return discount;
  };

  if (isPending) {
    return (
      <div className="order-details-container">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="order-details-container">
        <div className="error-card">
          <h2>Error Loading Order</h2>
          <p>{error}</p>
          <button className="button" onClick={() => navigate("/orders")}>
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  if (!order) {
    return null;
  }

  const discountTotal = getDiscountTotal();
  const deliveryFee = getDeliveryFee();

  return (
    <div className="order-details-container">
      <button className="back-button" onClick={() => navigate("/orders")}>
        ← Back to Orders
      </button>

      {/* Order Header */}
      <div className="order-header-card">
        <div className="order-header-top">
          <div className="order-title-section">
            <h1>Order Details</h1>
            <p className="order-date">{formatDate(order.orderTimestamp)}</p>
            {order.fulfillment?.fulfillmentDate && (
              <>
                <p className="pickup-date">
                  <strong>Scheduled Pickup Date:</strong>{" "}
                  {order.fulfillment.fulfillmentDate}
                </p>
                {order.orderState?.toLowerCase() !== "fulfilled" && (
                  <a
                    href="https://www.granddadsgroceries.com/pickup"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="pickup-instructions-link"
                  >
                    View Pickup Instructions →
                  </a>
                )}
              </>
            )}
          </div>
          <div className="order-status-section">
            <span
              className={`status-badge-large ${getStatusBadgeClass(
                order.orderState
              )}`}
            >
              {getStatusDisplayText(order.orderState)}
            </span>
            <p className="order-total-large">
              ${Number(order.orderTotal / 100).toFixed(2)}
            </p>
          </div>
        </div>
      </div>

      <div className="order-details-grid">
        {/* Left Column */}
        <div className="order-main-column">
          {/* Products Section */}
          <div className="order-section products-section">
            <h2>Order Items ({order.products.length})</h2>
            <div className="cart-items-list">
              {order.products.map((product) => handleRow(product))}
            </div>
          </div>
        </div>

        {/* Right Column - Sidebar */}
        <div className="order-sidebar">
          {/* Order Summary */}
          <div className="order-section summary-section">
            <h2>Order Summary</h2>
            <div className="summary-rows">
              <div className="summary-row">
                <span>Subtotal:</span>
                <span>${(calculateCartTotal() / 100).toFixed(2)}</span>
              </div>
              {deliveryFee > 0 && (
                <div className="summary-row">
                  <span>Delivery Fee:</span>
                  <span>${(deliveryFee / 100).toFixed(2)}</span>
                </div>
              )}
              {discountTotal > 0 && (
                <div className="summary-row promo-row">
                  <span>
                    Coupon Discount
                    {order.promotionCode?.code
                      ? ` (${order.promotionCode.code})`
                      : ""}
                    :
                  </span>
                  <span className="discount">
                    - ${(discountTotal / 100).toFixed(2)}
                  </span>
                </div>
              )}
              <div className="summary-row">
                <span>Tax:</span>
                <span>${(handleTaxTotal() / 100).toFixed(2)}</span>
              </div>
              <div className="summary-row total-row">
                <span>Total:</span>
                <span>${(Number(order.orderTotal || 0) / 100).toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetails;
